import { useCallback, useEffect, useRef, useState } from 'react';
import { InsertDriveFileOutlined, StickyNote2Outlined, TaskOutlined } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { DocumentReferenceObjectView, DocumentReferencesView } from 'api';
import { useStore } from 'common/store';
import WidgetDrawerProps from './WidgetDrawer.props';
import DRAWER, { TAB_IDS } from '../../../common/constants/drawer/Drawer.constants';
import { UseStoreValues } from '../../../common/types/store';
import { useDocuments, useLocalStorage, useResponse } from '../../../services/hooks';
import { Logger } from '../../../utilities';
import useGetFileIconByExtension from '../../../utilities/getFileIconByExtension/getFileIconByExtension';
import { Widgets } from '../../Widgets';
import DocumentPreviewDrawerTab from '../document-preview-drawer-tab/DocumentPreviewDrawerTab';
import NotesDrawerTabTitle from '../notes-drawer-tab-title/NotesDrawerTabTitle';
import NavigationConfig, { DrawerSecondaryAction } from '../tab-drawer/models/navigation-config.interface';
import TabDrawer from '../tab-drawer/TabDrawer';

const WidgetDrawer = ({
  documentReferencesProps,
  documentSourceProps,
  notesProps,
  spreadsheetNotesProps,
  applicationContext,
  contentWrapperSx,
  headerContentSx,
  documentTabCustomActions,
  documentApplicationStorageKey,
}: WidgetDrawerProps) => {
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const [navigationConfig, setNavigationConfig] = useState<NavigationConfig[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [documentNavItems, setDocumentNavItems] = useLocalStorage<{
    [docId: string]: string;
  }>(
    documentApplicationStorageKey
      ? `${documentApplicationStorageKey}`
      : `${companyInfo?.id}-${applicationContext}-doc-navigation-config`,
    {}
  );
  const { getDocumentById, downloadDocument } = useDocuments();
  const { errorNotification } = useResponse();

  // Create a hook for getting file icons that we can use safely
  const getFileIcon = useGetFileIconByExtension;

  // Track tabs that were explicitly closed by user (not auto-closed)
  const explicitlyClosedTabs = useRef<Set<string>>(new Set());

  // Store handleDocumentClick in a ref to avoid dependency issues
  const handleDocumentClickRef = useRef<
    (document: DocumentReferencesView, reference: DocumentReferenceObjectView) => void
      >(() => {});

  const handleTabClose = useCallback(
    (tabId: string) => {
      // Mark this tab as explicitly closed
      explicitlyClosedTabs.current.add(tabId);

      setDocumentNavItems(prev => {
        const { [tabId]: removedTab, ...rest } = prev ?? {};
        return rest;
      });

      // Remove the tab from navigation config immediately
      setNavigationConfig(prev => {
        const filteredConfig = prev.filter(item => item.uniqueId !== tabId);

        // If closing the active tab, determine the next active tab
        if (activeTabId === tabId) {
          // First try to find Document References tab
          const documentReferencesTab = filteredConfig.find(
            item => item.uniqueId === TAB_IDS.TABLE_DOCUMENT_REFERENCES
          );
          if (documentReferencesTab) {
            setActiveTabId(TAB_IDS.TABLE_DOCUMENT_REFERENCES);
          } else if (filteredConfig.length > 0) {
            // If Document References doesn't exist, use the first remaining tab
            setActiveTabId(filteredConfig[0].uniqueId);
          } else {
            setActiveTabId(null);
          }
        }

        return filteredConfig;
      });
    },
    [setDocumentNavItems, activeTabId]
  );

  const handleDownloadDocument = useCallback(
    async (document: DocumentReferencesView) => {
      await downloadDocument(document);
    },
    [downloadDocument]
  );

  const generateDocumentTab = useCallback(
    (document: DocumentReferencesView, fileIcon: React.ReactNode) => {
      const secondaryActions: DrawerSecondaryAction[] = [];

      secondaryActions.push({
        title: 'Download original',
        onClick: () => handleDownloadDocument(document),
      });

      if (documentTabCustomActions) {
        // Map custom actions to pass the entire document object
        documentTabCustomActions.forEach(action => {
          secondaryActions.push({
            ...action,
            onClick: () => action.onClick(document),
          });
        });
      }

      const citationNumber = documentSourceProps?.documentMetadata?.get(document.document_reference_id);

      const tabTitle = citationNumber ? `[${citationNumber}] - ${document.file.filename}` : document.file.filename;

      return {
        tabContent: <DocumentPreviewDrawerTab document={document} companyId={companyInfo?.id?.toString() ?? ''} />,
        tabTitle,
        uniqueId: document.document_reference_id.toString(),
        icon: fileIcon,
        onClose: handleTabClose,
        secondaryActions,
      };
    },
    [documentTabCustomActions, handleTabClose, handleDownloadDocument, companyInfo?.id, documentSourceProps]
  );

  const handleDocumentClick = useCallback(
    (document: DocumentReferencesView, reference: DocumentReferenceObjectView) => {
      const docId = document.document_reference_id.toString();
      const alreadyExists = documentNavItems[docId];

      if (alreadyExists) {
        // Just set this tab as active
        setActiveTabId(docId);
        return;
      }

      // Remove from explicitly closed tabs if user is reopening it
      explicitlyClosedTabs.current.delete(docId);

      setDocumentNavItems(prev => {
        const newNavItems = { ...prev };
        newNavItems[docId] = reference?.id?.toString() ?? docId;
        return newNavItems;
      });

      // Set the new tab as active
      setActiveTabId(docId);
    },
    [documentNavItems, setDocumentNavItems]
  );

  // Update the ref whenever handleDocumentClick changes
  handleDocumentClickRef.current = handleDocumentClick;

  // using react query to optimize for re-renders
  const documentTabRecords = documentNavItems;

  // Add a debounced version to prevent immediate queries after document clicks
  const [debouncedDocumentTabRecords, setDebouncedDocumentTabRecords] = useState(documentTabRecords);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDocumentTabRecords(documentTabRecords);
    }, 500); // 500ms delay to allow tab creation to complete

    return () => clearTimeout(timer);
  }, [documentTabRecords]);

  const {
    data: documentsData,
    isLoading: documentsLoading,
    error: documentsError,
  } = useQuery({
    queryKey: ['documents', Object.keys(debouncedDocumentTabRecords)],
    queryFn: async (): Promise<{ [documentId: string]: DocumentReferencesView }> => {
      const documentIds = Object.keys(debouncedDocumentTabRecords);
      if (!documentIds.length) {
        return {};
      }

      // this logic ensures that when a document is not referenced anymore, the tab is closed
      const promises = documentIds.map(async documentId => {
        try {
          const document = await getDocumentById(documentId);
          return { documentId, document };
        } catch (error) {
          Logger.error(`Error loading document ${documentId}`, error);
          throw new Error(`${documentId}`);
        }
      });

      const results = await Promise.allSettled(promises);
      const errors = results.filter(result => result.status === 'rejected');
      const successfulResults = results.filter(result => result.status === 'fulfilled');

      // Close tabs for documents that failed to load
      if (errors.length) {
        errors.forEach(error => {
          const documentId = error.reason.message;
          handleTabClose(documentId);
        });

        errorNotification(
          `Error loading documents for preview: ${errors.map(error => error.reason.message).join(', ')}`
        );
      }

      return successfulResults.reduce(
        (acc, { value: { documentId, document } }) => {
          // Only keep documents that are still in documentTabRecords
          // This prevents recreating manually closed tabs
          if (debouncedDocumentTabRecords[documentId]) {
            acc[documentId] = document;
          }
          return acc;
        },
        {} as { [documentId: string]: DocumentReferencesView }
      );
    },
    enabled: Object.keys(debouncedDocumentTabRecords).length > 0,
    // remove caching temporarily
    // staleTime: 1000 * 60 * 60,
  });

  const configureNavigationConfig = useCallback(async () => {
    const config: NavigationConfig[] = [];
    const iconSize = '1rem';

    if (notesProps) {
      config.push({
        tabContent: <Widgets notesProps={notesProps} />,
        tabTitle: (
          <NotesDrawerTabTitle
            pageType={notesProps.pageType}
            pageTypeKey={notesProps.pageTypeKey}
            pageTypeId={notesProps.pageTypeId}
            notesFallback={notesProps.notes}
          />
        ),
        uniqueId: TAB_IDS.TABLE_NOTES,
        icon: <StickyNote2Outlined sx={{ height: iconSize, width: iconSize }} />,
      });
    }

    if (spreadsheetNotesProps) {
      config.push({
        tabContent: <Widgets spreadsheetNotesProps={spreadsheetNotesProps} />,
        tabTitle: DRAWER.TABS.SPREADSHEET_NOTES,
        uniqueId: TAB_IDS.TABLE_SPREADSHEET_NOTES,
        icon: <TaskOutlined sx={{ height: iconSize, width: iconSize }} />,
      });
    }

    if (documentReferencesProps) {
      config.push({
        tabContent: (
          <Widgets
            documentReferencesProps={{
              ...documentReferencesProps,
            }}
            documentSourceProps={documentSourceProps}
            onDocumentClick={handleDocumentClickRef.current!}
          />
        ),
        tabTitle: DRAWER.TABS.DOCUMENT_REFERENCES,
        uniqueId: TAB_IDS.TABLE_DOCUMENT_REFERENCES,
        icon: <InsertDriveFileOutlined sx={{ height: iconSize, width: iconSize }} />,
      });
    }

    // add document tabs from React Query data
    if (documentsData) {
      Object.entries(documentsData).forEach(([, document]) => {
        const documentId = document.document_reference_id.toString();

        // Skip explicitly closed tabs
        if (explicitlyClosedTabs.current.has(documentId)) {
          return;
        }

        const fileIcon = getFileIcon(document.file.file_type);
        const tab = generateDocumentTab(document, fileIcon);
        config.push(tab);
      });
    }

    if (!config.length) {
      return;
    }

    setNavigationConfig(prev => {
      // Get the unique IDs of the new document tabs we're about to add
      const newDocumentTabIds = new Set(
        config.filter(tab => !Object.values(TAB_IDS).includes(tab.uniqueId as TAB_IDS)).map(tab => tab.uniqueId)
      );

      // Preserve existing document tabs that aren't being replaced
      const existingDocumentTabs = prev.filter(
        tab => !Object.values(TAB_IDS).includes(tab.uniqueId as TAB_IDS) && !newDocumentTabIds.has(tab.uniqueId)
      );

      return [...config, ...existingDocumentTabs];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentNavItems, documentSourceProps, notesProps, spreadsheetNotesProps, applicationContext, documentsData]);

  useEffect(() => {
    if (!documentsLoading) {
      configureNavigationConfig();
    }

    if (documentsError) {
      Logger.error('Error loading documents', documentsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentReferencesProps,
    documentSourceProps,
    notesProps,
    spreadsheetNotesProps,
    documentsData,
    documentsLoading,
    documentsError,
  ]);

  if (documentsLoading) {
    return <div>Loading documents...</div>;
  }

  if (!navigationConfig.length) {
    return null;
  }

  return (
    <TabDrawer
      navigationConfig={navigationConfig}
      activeTabId={activeTabId}
      onTabSelect={setActiveTabId}
      contentWrapperSx={contentWrapperSx}
      headerContentSx={headerContentSx}
    />
  );
};

export default WidgetDrawer;
